import { useContext, useEffect, useState } from 'react';
import useFunctionKeys from '../../../hooks/useFunctionKeys';
import InfoAreaDrawer from '../../../uiControls/infoArea/InfoAreaDrawer';
import { Search } from "../../../filtering";
import { pimsShipmentList, TableColumn, TableSettings } from '../../../tableSettings';
import AbstractListView, {
  DataFetchResult,
  ExportRowRenderer,
  ListCustomFilterRenderer,
  ListOrderBy,
  ListRowRenderer
} from "../AbstractListView";

import JobListViewContext from '../JobListViewContext';

import {
  DateRenderer,
  DateTimeRenderer,
  FloatRenderer,
  IndicatorRenderer,
  NullableCheckboxIconRenderer,
  TextAlignLeftRenderer,
  TextAlignRightRenderer,
  TextRenderer,
} from '../../../uiControls/SmartTableRenderers';

import { JobNames } from '../../../framework.core/JobNames';
import ListViewShortcutMenu from '../ListViewShortcutMenu';
import { PimsPoListItem } from '../../../fms.data/models/PimsPoListItem';
import * as XCol from '../../../framework.core/XExportColumn';

function formatData(data: Array<PimsPoListItem>) {
  return data;
}

// function useSelectedDownloadItem() {
//   const jobListContext = useContext(JobListViewContext);

//   const selectedElement = jobListContext.selectedElement as PimsPoListItem | null;

//   function view() {
//     jobListContext.showPopupRow();
//   }

//   return { selectedElement, view };
// }

function DownloadListCommands() {
  return (
    <>
    </>
  );
}

function DownloadListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ShipmentListViewExtras() {
  useFunctionKeys({ onFunction: onFunctionKey });

  const jobListContext = useContext(JobListViewContext);

  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
    if (!unprocessedFkey) {
      return;
    }

    doProcessFkey();
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    } else if (unprocessedFkey === "F8") {
      jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');;
    }

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F11', 'F8'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    <InfoAreaDrawer
      showContent={jobListContext.infoAreaContent}
      jobName={JobNames.OiPims}
      jobId={(jobListContext.selectedElement as PimsPoListItem | null)?.id ?? 0}
      onClose={() => jobListContext.setInfoAreaContent('none')}
    />
  );
}

/**
 * Concrete implementation for Download List screen
 *
 * @class ShipmentListView
 * @extends {AbstractListView}
 */
class ShipmentListView extends AbstractListView {
  private jobName: JobNames.OiSoPims | JobNames.OeSoPims | JobNames.AiSoPims | JobNames.AeSoPims;

  constructor(jobName: JobNames.OiSoPims | JobNames.OeSoPims | JobNames.AiSoPims | JobNames.AeSoPims) {
    super();
    this.jobName = jobName;
  }

  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(this.jobName,
      'PimsPoListItem',
      PimsPoListItem.calculateSearchParameters(search, PimsPoListItem.MapSearchFields),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch,
      );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return { data: [], totalCount: 0 };
    }

    return { data: formatData(apiResult.entities), totalCount: apiResult.totalCount };
  }


  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
    const apiResult = await api.getListItems(this.jobName, 'PimsPoListItem',
      PimsPoListItem.calculateSearchParameters(search, PimsPoListItem.MapSearchFields),
      pageNum,
      limit,
      pageSize,
      pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return  formatData(apiResult.entities);
  }
  

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
    ];
  }

  getRowRenderers(): ListRowRenderer[] {
    return PimsPoListItem.getExportCollection().map(x => {
      const colRenderer: ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if (x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      if (x.align === XCol.XAlignType.Right) colRenderer.Cell = TextAlignRightRenderer;
      if (x.dataIndex === "status") colRenderer.Cell = IndicatorRenderer;
      else if (x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if (x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if (x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if (x.type === XCol.XCellType.Double) {
        if (x.dataIndex === 'totQty')
          colRenderer.Cell = (props: any) => <FloatRenderer {...props} decimals={0} />;
        else
          colRenderer.Cell = FloatRenderer;
      }
      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    colRenderer.push({ accessor: 'totQty', changedValue: (value: any) => { return value['totQty'] } });
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'id',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings = pimsShipmentList;
    const cols = PimsPoListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type };
    });
    return tsettings;
  }

  renderCommands() {
    return (
      <DownloadListCommands />
    );
  }

  renderBeforeConfigureButton() {
    return (
      <DownloadListToolbar />
    );
  }

  renderExtras() {
    return <ShipmentListViewExtras />
  }

  getViewUrlFor() {
    return '';
  }

  getListViewTitle() {
    return 'Shipping Order List';
  };
};

export default ShipmentListView;